import { VerifyPaymentErrorScreen, VerifyPaymentScreen } from "@/features/auth/screens/VerifyPaymentScreen";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { supabaseClient } from "@/lib/supabase";

const PLAN_VALUES: Record<string, number> = {
    starter: 30,
    pro: 50,
    enterprise: 0,
    trial: 0,
};

function trackPurchase(sessionId: string, plan: string | null) {
    const value = plan ? (PLAN_VALUES[plan] ?? 30) : 30;
    const itemId = plan || "starter";
    const itemName = plan === "pro" ? "Patient Watch Pro" : "Patient Watch Starter";

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
        event: "purchase",
        ecommerce: {
            transaction_id: sessionId,
            value,
            currency: "GBP",
            items: [
                {
                    item_id: itemId,
                    item_name: itemName,
                    item_category: "Subscription",
                    price: value,
                    quantity: 1,
                },
            ],
        },
    });
    // Also fire via gtag if available (covers both GA4 and any linked Google Ads)
    if (typeof (window as any).gtag === "function") {
        (window as any).gtag("event", "purchase", {
            transaction_id: sessionId,
            value,
            currency: "GBP",
            items: [
                {
                    item_id: itemId,
                    item_name: itemName,
                    item_category: "Subscription",
                    price: value,
                    quantity: 1,
                },
            ],
        });
    }
}

const VerifyPaymentError = ({ error }: { error: Error }) => {
    return <VerifyPaymentErrorScreen message={error.message} />;
};

export const Route = createFileRoute("/_auth/verify-payment")({
    component: VerifyPaymentScreen,
    errorComponent: VerifyPaymentError,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            session_id: (search?.session_id as string) ?? "",
        };
    },
    beforeLoad: async ({ search }) => {
        const session_id = search.session_id as string;

        if (!session_id) {
            throw new Error("Missing payment session ID. Please try again.");
        }

        // Wait for session to be restored from localStorage
        const {
            data: { session },
        } = await supabaseClient.auth.getSession();

        if (!session) {
            // Session not found - wait a bit and try again
            await new Promise((resolve) => setTimeout(resolve, 1000));
            const {
                data: { session: retrySession },
            } = await supabaseClient.auth.getSession();

            if (!retrySession) {
                throw new Error("No authenticated session found. Please try signing up again.");
            }
        }

        // Query billing table for payment record created by Stripe webhook
        // The webhook already verified payment, so if record exists = payment successful
        const { data: billing, error: billingError } = await supabaseClient
            .from("billing")
            .select("user_id, subscription_plan, users!inner(email, first_name, last_name)")
            .eq("stripe_session_id", session_id)
            .single();

        if (billingError || !billing) {
            // Webhook might still be processing - retry once
            await new Promise((resolve) => setTimeout(resolve, 2000));

            const { data: retryBilling, error: retryError } = await supabaseClient
                .from("billing")
                .select("user_id, subscription_plan, users!inner(email, first_name, last_name)")
                .eq("stripe_session_id", session_id)
                .single();

            if (retryError || !retryBilling) {
                throw new Error("Payment verification in progress. Please wait a moment and refresh the page.");
            }

            // Use retry data - payment confirmed, fire conversion tracking
            trackPurchase(session_id, retryBilling.subscription_plan);
            const retryUserData = retryBilling.users as any;
            const retryRedirectOptions: any = {
                to: "/verify-email",
                search: {
                    email: retryUserData.email,
                    first_name: retryUserData.first_name || "",
                    last_name: retryUserData.last_name || "",
                },
            };
            throw redirect(retryRedirectOptions);
        }

        // Success - payment confirmed, fire conversion tracking
        trackPurchase(session_id, billing.subscription_plan);
        const userData = billing.users as any;
        const redirectOptions: any = {
            to: "/verify-email",
            search: {
                email: userData.email,
                first_name: userData.first_name || "",
                last_name: userData.last_name || "",
            },
        };
        throw redirect(redirectOptions);
    },
});
